import { BRAND } from './brand.js'
import type { MessageKey } from './i18n.js'

/**
 * Error notices — ADD-004, OPS-05 (§15.4).
 *
 * Every error state shows three things: what failed, what to do next, and where to read more. The
 * recovery action is the one the service chose (the error envelope's `recovery_action`), so a
 * surface never invents its own advice, and the help link carries the error code so a support
 * article can be reached without the person describing the failure themselves.
 */

/** The registry's recovery actions, as message keys. An unknown action still gets an honest line. */
const RECOVERY_MESSAGE_KEYS: Readonly<Record<string, MessageKey>> = {
  retry_later: 'error.recovery.retry_later',
  resend_valid_json: 'error.recovery.retry_later',
  fix_request_shape: 'error.recovery.fix_input',
  fix_tool_arguments: 'error.recovery.fix_input',
  fix_tool_arguments_or_retry: 'error.recovery.fix_input',
  use_server_discover: 'error.recovery.fix_input',
  send_matching_headers: 'error.recovery.fix_input',
  use_advertised_protocol_version: 'error.recovery.update_app',
  reload_or_reopen: 'error.recovery.reload',
  reopen_from_home: 'error.recovery.reload',
  wait_or_void_a_request: 'error.recovery.free_a_slot',
  remove_extra_signer: 'error.recovery.remove_signer',
  share_it_myself: 'error.recovery.share_manually',
  upgrade: 'error.recovery.upgrade',
  buy_credits: 'error.recovery.upgrade',
  sign_in_again: 'error.recovery.sign_in_again',
  link_account: 'error.recovery.link_account',
  link_account_for_account_data: 'error.recovery.link_account',
  check_your_connection: 'error.recovery.check_connection',
  // S5.5's operational refusals each promise a specific way forward, so they map to their own line
  // rather than to the generic "contact support".
  upgrade_or_void_and_recreate: 'error.recovery.upgrade_or_void_and_recreate',
  share_the_existing_link: 'error.recovery.share_the_existing_link',
  wait_then_resend: 'error.recovery.wait_then_resend',
  upgrade_or_new_request: 'error.recovery.upgrade_or_new_request',
  choose_later_deadline: 'error.recovery.choose_later_deadline',
  wait_for_completion: 'error.recovery.wait_for_completion',
  // S5.8 reuses two existing actions: an attachment read the reviewed contract has not opened is
  // "not authorized", and a slot that expired before its bytes landed is a retry of the same file.
  not_authorized: 'error.recovery.not_authorized',
  retry_same_request: 'error.recovery.retry_same_request',
  // S6.5's error-catalogue pass. Every remaining registry action a person-facing surface can
  // actually render now has its own sentence — what happened, what to do next, and whether a retry
  // is safe — so a reachable refusal never downgrades to the generic fallback. The registry carries
  // these as full `error.recovery.*` keys; registering the bare id here lets the alias table below
  // resolve both spellings.
  //
  // Plan limits and request lifecycle (product codes).
  wait_void_upgrade: 'error.recovery.wait_void_upgrade',
  reduce_signers_or_upgrade: 'error.recovery.reduce_signers_or_upgrade',
  share_it_myself_or_upgrade: 'error.recovery.share_it_myself_or_upgrade',
  share_it_myself_or_buy_pack: 'error.recovery.share_it_myself_or_buy_pack',
  create_new_request: 'error.recovery.create_new_request',
  view_current_state: 'error.recovery.view_current_state',
  refresh_state: 'error.recovery.refresh_state',
  retry_support_code: 'error.recovery.retry_support_code',
  refresh_balance: 'error.recovery.refresh_balance',
  refresh_or_reconcile: 'error.recovery.refresh_or_reconcile',
  reconnect_destination: 'error.recovery.reconnect_destination',
  retry_or_change_location: 'error.recovery.retry_or_change_location',
  // PDF import and composition (OP_PDF_*).
  remove_password_or_export_plain: 'error.recovery.remove_password_or_export_plain',
  choose_another_file: 'error.recovery.choose_another_file',
  flattened_export_guidance: 'error.recovery.flattened_export_guidance',
  invalidate_or_reject: 'error.recovery.invalidate_or_reject',
  split_or_reduce_file: 'error.recovery.split_or_reduce_file',
  split_document: 'error.recovery.split_document',
  downscale_image: 'error.recovery.downscale_image',
  export_to_pdf_locally: 'error.recovery.export_to_pdf_locally',
  retry_compose: 'error.recovery.retry_compose',
  retry_or_manual_fields: 'error.recovery.retry_or_manual_fields',
  // Delivery (OP_DELIVERY_*).
  retry_backoff: 'error.recovery.retry_backoff',
  manual_share: 'error.recovery.manual_share',
  wait_or_manual: 'error.recovery.wait_or_manual',
  // `none_required` is the registry's "the service already retries this" action, shared by a lost
  // delivery claim and by the scaffold's 501, so both get the same honest "nothing to do" line.
  none_required: 'error.recovery.none_required',
  // Purchases (OP_PURCHASE_*).
  retry_or_contact_support: 'error.recovery.retry_or_contact_support',
  restore_or_contact_support: 'error.recovery.restore_or_contact_support',
  // Auth challenge (OP_AUTH_CHALLENGE_INVALID).
  request_new_code: 'error.recovery.request_new_code',
  // Backup and object storage (OP_BACKUP_*, OP_STORAGE_*).
  free_space_or_change_location: 'error.recovery.free_space_or_change_location',
  retry_backup: 'error.recovery.retry_backup',
  choose_another_backup: 'error.recovery.choose_another_backup',
  check_backup_passphrase: 'error.recovery.check_backup_passphrase',
  update_app_for_backup: 'error.recovery.update_app_for_backup',
  free_local_space: 'error.recovery.free_local_space',
  restore_from_backup: 'error.recovery.restore_from_backup',
  recover_previous_revision: 'error.recovery.recover_previous_revision',
  reload_and_merge: 'error.recovery.reload_and_merge',
  // Finalization (OP_FINALIZE_*).
  reprepare_finalize: 'error.recovery.reprepare_finalize',
  refresh_and_retry: 'error.recovery.refresh_and_retry',
  incomplete_evidence_export: 'error.recovery.incomplete_evidence_export',
  wait_for_signers: 'error.recovery.wait_for_signers',
  // Generic transport and validation. `reload` reuses the sentence `reload_or_reopen` already had.
  reload: 'error.recovery.reload',
  fix_field_errors: 'error.recovery.fix_field_errors',
  sign_in: 'error.recovery.sign_in',
  view_available_items: 'error.recovery.view_available_items',
  reduce_payload: 'error.recovery.reduce_payload',
  // OP_RATE_LIMITED carries `error.backoff_retry` as its action — the one entry that is neither a
  // bare id nor an `error.recovery.*` key — so both spellings are registered.
  backoff_retry: 'error.recovery.backoff_retry',
  'error.backoff_retry': 'error.recovery.backoff_retry',
  contact_support: 'error.recovery.contact_support',
}

/**
 * Some registry entries carry the full message key as their recovery action (S5.5/S5.8's newer
 * refusals) while older ones carry a bare action id. Both name the same promise, so the table is
 * indexed under both spellings rather than making every caller guess which form it received.
 */
const RECOVERY_KEYS_WITH_ALIASES: Readonly<Record<string, MessageKey>> = {
  ...RECOVERY_MESSAGE_KEYS,
  ...Object.fromEntries(
    Object.entries(RECOVERY_MESSAGE_KEYS).map(([action, key]) => [`error.recovery.${action}`, key]),
  ),
}

export interface ErrorNoticeFacts {
  readonly code: string
  /** The service's own recovery action, when the response carried one. */
  readonly recovery_action: string | null
  readonly retryable: boolean
}

export function recoveryMessageKey(action: string | null): MessageKey {
  if (action === null) return 'error.recovery.retry_later'
  return RECOVERY_KEYS_WITH_ALIASES[action] ?? 'error.recovery.contact_support'
}

/**
 * The way to ask for help with an error: an email to support, since there is no help center. Only
 * the error code and the recovery action travel — never a document id, an address or a
 * capability — as machine lines the person can read before sending.
 */
export function helpUrlFor(facts: ErrorNoticeFacts): string {
  const lines = [`error=${facts.code}`]
  if (facts.recovery_action !== null) lines.push(`action=${facts.recovery_action}`)
  const subject = `${BRAND.product_name} ${facts.code}`
  return (
    `mailto:${BRAND.exception_contact}` +
    `?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(`${lines.join('\n')}\n`)}`
  )
}

/** The full notice text: what happened and what to do, in that order. */
export function noticeFor(
  facts: ErrorNoticeFacts,
  t: (key: MessageKey, params?: Readonly<Record<string, string | number>>) => string,
): {
  readonly what: string
  readonly next: string
  readonly help_url: string
  /** The support code, ready to render on its own muted line (`ErrorCode`), never inside `what`. */
  readonly code_line: string
} {
  return {
    what: t('error.notice.what'),
    next: t(recoveryMessageKey(facts.recovery_action)),
    help_url: helpUrlFor(facts),
    code_line: t('error.code_line', { code: facts.code }),
  }
}
